import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type {
  Participant,
  ParticipantGroup,
  PartyState,
} from "@pineseed/gui/types";
// State atoms must NEVER import integrations or handlers directly
// Integration will be called via handler callback pattern
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";
import { partyStructureSchemas, tagsSchemas } from "@pineseed/spec";

// Import ServerTag type for tags update callback
type ServerTag = z.infer<typeof tagsSchemas.Tag>;
import type { z } from "zod";

/**
 * Party state atoms for workspace participants
 * STRICT RULE: This module contains ONLY atoms - no business logic, no UI code, no external system calls
 *
 * INTEGRATION ARCHITECTURE:
 * - Local state: Persisted UI state (collapsed sections, etc.)
 * - Server state: Live party data from server via SSE
 * - Hybrid approach: Local state for UI, server state for data
 */

// Local UI state (persisted)
export const partyUIStateAtom = atomWithStorage<
  Pick<PartyState, "isPartySectionCollapsed">
>("ive-party-ui-state", { isPartySectionCollapsed: false });

// Server-side party data (live, not persisted)
export const serverPartyDataAtom = atom<{
  participants: Participant[];
  groups: ParticipantGroup[];
  stats?: {
    totalParticipants: number;
    onlineParticipants: number;
    totalGroups: number;
    participantsByStatus?: Record<string, number>;
    participantsByType?: Record<string, number>;
  };
  typingContext?: {
    conversationId?: string;
    threadId?: string;
  };
  lastUpdated: Date | null;
}>({
  participants: [],
  groups: [],
  lastUpdated: null,
});

// Integration status
export const partyIntegrationStatusAtom = atom<{
  isEnabled: boolean;
  connectionStatus: "connected" | "disconnected" | "reconnecting";
  lastError: Error | null;
}>({
  isEnabled: false,
  connectionStatus: "disconnected",
  lastError: null,
});

// Combined party state (merges UI state with server data)
export const partyStateAtom = atom(
  (get) => {
    const uiState = get(partyUIStateAtom);
    const serverData = get(serverPartyDataAtom);

    return {
      participants: serverData.participants,
      groups: serverData.groups,
      isPartySectionCollapsed: uiState.isPartySectionCollapsed,
    } as PartyState;
  },
  (get, set, update: Partial<PartyState>) => {
    // Only allow UI state updates (participants/groups come from server)
    if ("isPartySectionCollapsed" in update) {
      const currentUIState = get(partyUIStateAtom);
      set(partyUIStateAtom, {
        ...currentUIState,
        isPartySectionCollapsed: update.isPartySectionCollapsed!,
      });
    }
  },
);

// Derived atoms for specific state slices (read-only for server data)
export const participantsAtom = atom(
  (get) => get(serverPartyDataAtom).participants,
);
export const participantGroupsAtom = atom(
  (get) => get(serverPartyDataAtom).groups,
);

export const typingContextAtom = atom(
  (get) => get(serverPartyDataAtom).typingContext,
);

export const partySectionCollapsedAtom = atom(
  (get) => get(partyUIStateAtom).isPartySectionCollapsed,
  (get, set, collapsed: boolean) => {
    const currentUIState = get(partyUIStateAtom);
    set(partyUIStateAtom, {
      ...currentUIState,
      isPartySectionCollapsed: collapsed,
    });
  },
);

// Derived atoms for organized data
export const participantsByGroupAtom = atom((get) => {
  const participants = get(participantsAtom);
  const groups = get(participantGroupsAtom);

  console.log("🎭 State: participantsByGroupAtom computing with:", {
    participantCount: participants.length,
    groupCount: groups.length,
  });

  // Group participants by their groupId
  const grouped = participants.reduce(
    (acc, participant) => {
      const groupId = participant.groupId || "ungrouped";
      if (!acc[groupId]) {
        acc[groupId] = [];
      }
      acc[groupId].push(participant);
      return acc;
    },
    {} as Record<string, Participant[]>,
  );

  // Add group information
  const result: Array<{
    group: ParticipantGroup | null;
    participants: Participant[];
  }> = [];

  // Add grouped participants
  groups.forEach((group) => {
    result.push({
      group,
      participants: grouped[group.id] || [],
    });
  });

  // Add ungrouped participants
  if (grouped.ungrouped && grouped.ungrouped.length > 0) {
    result.push({
      group: null,
      participants: grouped.ungrouped,
    });
  }

  console.log("🎭 State: participantsByGroupAtom result:", {
    groupCount: result.length,
    totalParticipants: result.reduce(
      (sum, g) => sum + g.participants.length,
      0,
    ),
  });

  return result;
});

// Party handler callbacks atom for proper communication pattern
// This follows the mandatory callback pattern - handlers communicate with state via callbacks
export const partyHandlerCallbacksAtom = atom<{
  onInitializePartySystem?: (
    workspaceId: string,
    authToken?: string,
  ) => Promise<void>;
  onShutdownPartySystem?: () => Promise<void>;
  // Note: Manual refresh removed - party integration now handles real-time updates via SSE
}>({});

// Atom to initialize party system via handler (follows callback pattern)
export const initializePartyIntegrationAtom = atom(
  null,
  async (get, _set, config: PartyIntegrationConfig) => {
    try {
      console.log(
        "🎭 State: Requesting party system initialization via handler callback",
      );

      // Trigger handler to initialize party system - this follows proper callback pattern
      const callbacks = get(partyHandlerCallbacksAtom);
      if (callbacks.onInitializePartySystem) {
        await callbacks.onInitializePartySystem(
          config.workspaceId || DEFAULT_WORKSPACE_ID,
          config.authToken,
        );
        console.log(
          "✅ State: Party system initialization requested via handler",
        );
      } else {
        console.warn(
          "⚠️ State: Party handler not available for initialization",
        );
        throw new Error("Party handler not available for initialization");
      }
    } catch (error) {
      console.error("❌ State: Failed to initialize party system:", error);
      throw error;
    }
  },
);

// Atom to shutdown party system via handler (follows callback pattern)
export const shutdownPartyIntegrationAtom = atom(null, async (get, _set) => {
  try {
    console.log(
      "🎭 State: Requesting party system shutdown via handler callback",
    );

    // Trigger handler to shutdown party system - this follows proper callback pattern
    const callbacks = get(partyHandlerCallbacksAtom);
    if (callbacks.onShutdownPartySystem) {
      await callbacks.onShutdownPartySystem();
      console.log("✅ State: Party system shutdown requested via handler");
    } else {
      console.warn("⚠️ State: Party handler not available for shutdown");
    }
  } catch (error) {
    console.error("❌ State: Failed to shutdown party system:", error);
  }
});

// Atoms to receive updates from handler via callback pattern
// These are called by the handler - state NEVER calls handler directly
export const updatePartyDataAtom = atom(
  null,
  (
    _get,
    set,
    party: WorkspacePartyResponse & {
      typingContext?: {
        conversationId?: string;
        threadId?: string;
      };
    },
  ) => {
    console.log("🎭 State: updatePartyDataAtom called with party data:", {
      participantCount: party.participants?.length || 0,
      groupCount: party.groups?.length || 0,
      hasStats: !!party.stats,
      hasParticipantsByGroup: !!party.participantsByGroup,
      hasTypingContext: !!party.typingContext,
      typingThreadId: party.typingContext?.threadId,
    });

    // Transform server data to match UI types (handle Date conversion)
    const transformedParticipants = party.participants.map(
      (p) => ({
        ...p,
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt),
        lastSeen: p.lastSeen === null ? null : new Date(p.lastSeen),
      }),
    );

    const transformedGroups = party.groups.map(
      (g: {
        id: string;
        name: string;
        description?: string;
        color?: string;
        isExpanded?: boolean;
        workspaceId: string;
        createdAt: string;
        updatedAt: string;
      }) => ({
        ...g,
        createdAt: new Date(g.createdAt),
        updatedAt: new Date(g.updatedAt),
      }),
    );

    set(serverPartyDataAtom, {
      participants: transformedParticipants,
      groups: transformedGroups,
      stats: party.stats,
      typingContext: party.typingContext,
      lastUpdated: new Date(),
    });

    console.log("🎭 State: serverPartyDataAtom updated successfully");
  },
);

export const updatePartyConnectionStatusAtom = atom(
  null,
  (_get, set, status: "connected" | "disconnected" | "reconnecting") => {
    set(partyIntegrationStatusAtom, (prev) => ({
      ...prev,
      connectionStatus: status,
      isEnabled: status === "connected",
    }));
    console.log("🎭 State: Party integration status updated");
  },
);

export const updatePartyErrorAtom = atom(null, (_get, set, error: Error) => {
  console.error("🎭 State: Received error update from handler:", error);
  set(partyIntegrationStatusAtom, (prev) => ({
    ...prev,
    lastError: error,
  }));
});

export const updatePartyTagsAtom = atom(
  null,
  async (_get, set, tags: ServerTag[]) => {
    console.log(
      "🎭 State: Received tags update from handler:",
      tags.length,
      "tags",
    );
    // Update server tags in workspace state
    const { updateServerTagsAtom } = await import("../state/workspaces");
    set(updateServerTagsAtom, tags);
  },
);

// Legacy atoms for backward compatibility (now read-only for server data)
export const addParticipantAtom = atom(
  null,
  (
    _get,
    _set,
    _participant: Omit<Participant, "id" | "createdAt" | "updatedAt">,
  ) => {
    console.warn(
      "addParticipantAtom: Server-side party management is enabled. Use server API to add participants.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

export const updateParticipantAtom = atom(
  null,
  (_get, _set, _participantId: string, _updates: Partial<Participant>) => {
    console.warn(
      "updateParticipantAtom: Server-side party management is enabled. Use server API to update participants.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

export const removeParticipantAtom = atom(
  null,
  (_get, _set, _participantId: string) => {
    console.warn(
      "removeParticipantAtom: Server-side party management is enabled. Use server API to remove participants.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

export const addParticipantGroupAtom = atom(
  null,
  (
    _get,
    _set,
    _group: Omit<ParticipantGroup, "id" | "createdAt" | "updatedAt">,
  ) => {
    console.warn(
      "addParticipantGroupAtom: Server-side party management is enabled. Use server API to add groups.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

export const updateParticipantGroupAtom = atom(
  null,
  (_get, _set, _groupId: string, _updates: Partial<ParticipantGroup>) => {
    console.warn(
      "updateParticipantGroupAtom: Server-side party management is enabled. Use server API to update groups.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

export const removeParticipantGroupAtom = atom(
  null,
  (_get, _set, _groupId: string) => {
    console.warn(
      "removeParticipantGroupAtom: Server-side party management is enabled. Use server API to remove groups.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

export const toggleGroupExpansionAtom = atom(
  null,
  (_get, _set, _groupId: string) => {
    console.warn(
      "toggleGroupExpansionAtom: Server-side party management is enabled. Use server API to update group expansion.",
    );
    // In a full implementation, this would make an API call to the server
  },
);

// Extract types from generated schemas
type WorkspacePartyResponse = z.infer<
  typeof partyStructureSchemas.WorkspacePartyResponse
>;

// Configuration type for party integration
interface PartyIntegrationConfig {
  workspaceId?: string;
  authToken?: string;
}
